import React from 'react';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { updateChild } from '../../thunk';

import type { ChildJson } from '@/types/domain/child/entities/childEntity';
import type { GenderType } from '@/types/domain/child/valueObjects/gender';

import DateFieldComp from '@/components/DateFieldComp';
import DialogTitle from '@/components/DialogTitle';
import DialogTransition from '@/components/DialogTransition';
import SelectFieldComp from '@/components/SelectFieldComp';
import TextFieldComp from '@/components/TextFieldComp';
import { Birthday } from '@/domain/child/service/entity/valueObjects/birthday';
import { ChildId } from '@/domain/child/service/entity/valueObjects/childId';
import { FamilyName } from '@/domain/child/service/entity/valueObjects/familyName';
import { FamilyNameKana } from '@/domain/child/service/entity/valueObjects/familyNameKana';
import { Gender } from '@/domain/child/service/entity/valueObjects/gender';
import { Name } from '@/domain/child/service/entity/valueObjects/name';
import { NameKana } from '@/domain/child/service/entity/valueObjects/nameKana';

/**
 * プロパティ
 */
export type Props = {
  /** 子ども */
  child: ChildJson,
  /** 開いているかセットする */
  setIsOpen: (isOpen: boolean) => void,
};

type ChildJsonInput = {
  childId: string,
  name: string,
  nameKana: string,
  familyName: string,
  familyNameKana: string,
  gender: GenderType,
  birthday: string,
};

const Edit: React.FC<Props> = ({ child, setIsOpen }) => {
  const dispatch = useDispatch();
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<ChildJsonInput>({
    defaultValues: {
      childId: child.childId,
      name: child.name,
      nameKana: child.nameKana,
      familyName: child.familyName,
      familyNameKana: child.familyNameKana,
      birthday: new Date(child.birthday).toISOString().split('T')[0],
      gender: child.gender,
    },
  });
  const onSubmit = (input: ChildJsonInput) => {
    dispatch(updateChild({
      parentId: child.parentId,
      childId: input.childId,
      name: input.name,
      nameKana: input.nameKana,
      familyName: input.familyName,
      familyNameKana: input.familyNameKana,
      gender: input.gender,
      birthday: input.birthday,
    }));
    setIsOpen(false);
  };
  return (
    <Dialog
      open
      onClose={() => setIsOpen(false)}
      fullScreen
      TransitionComponent={DialogTransition}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle title="子ども情報の編集" />
        <DialogContent>
          <p>子どもの苗字、名前、苗字（ひらがな）、名前（ひらがな）、誕生日、性別を入力してください。</p>
          <p>※子どもIDは変更できません。変更する際は、子どもの登録を解除して、子どもの新規登録をする必要があります。</p>
          <div style={{ margin: '10px 0' }}>
            <TextFieldComp
              type={ChildId.type}
              control={control}
              rules={ChildId.validationRule}
              name={ChildId.key}
              label={ChildId.label}
              placeholder={ChildId.placeholder}
              errors={errors}
              disabled
            />
          </div>
          <div
            style={{
              marginTop: '10px',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '10px',
            }}
          >
            <TextFieldComp
              type={FamilyName.type}
              control={control}
              rules={FamilyName.validationRule}
              name={FamilyName.key}
              label={FamilyName.label}
              placeholder={FamilyName.placeholder}
              errors={errors}
            />
            <TextFieldComp
              type={Name.type}
              control={control}
              rules={Name.validationRule}
              name={Name.key}
              label={Name.label}
              placeholder={Name.placeholder}
              errors={errors}
            />
            <TextFieldComp
              type={FamilyNameKana.type}
              control={control}
              rules={FamilyNameKana.validationRule}
              name={FamilyNameKana.key}
              label={FamilyNameKana.label}
              placeholder={FamilyNameKana.placeholder}
              errors={errors}
            />
            <TextFieldComp
              type={NameKana.type}
              control={control}
              rules={NameKana.validationRule}
              name={NameKana.key}
              label={NameKana.label}
              placeholder={NameKana.placeholder}
              errors={errors}
            />
            <DateFieldComp
              name={Birthday.key}
              control={control}
              label={Birthday.label}
              rules={Birthday.validationRule}
              errors={errors}
            />
            <SelectFieldComp
              control={control}
              name={Gender.key}
              label={Gender.label}
              rules={Gender.validationRule}
              errors={errors}
              items={Gender.list}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" type="submit" color="primary">
            保存する
          </Button>
          <Button variant="contained" onClick={() => setIsOpen(false)} color="primary">
            閉じる
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default Edit;
